import React, { useMemo } from "react";
import InventoryKPI from "./InventoryKPI";
import useQueryWithRetries from "../../../../Tools/useQueryWithRetries";
import { GET_INVENTORY_FINDINGS_BY_DATASOURCES } from "../../../WidgetDataApi";
import useWidgetFilterNavigator from "../../../useWidgetFilterNavigator";
import { useIcon, formatNumber } from "@SeemplicityDev/shared";
import type { SelectedScope } from "../../../../types/scope";
import type { EntityItem } from "../../../../Components/EntityLogo/EntityList";

interface InventoryFindingsByDatasourcesProps {
  selectedScope?: SelectedScope | null;
  filterToExecute?: string | null;
  scopeToExecute?: string | null;
  isForExport?: boolean;
  setIsReadyToExport?: ((isReady: boolean) => void) | null;
}

interface DatasourceDefinitionAttribute {
  id?: string | number;
  name?: string;
  display_name?: string;
}

interface SourcesMetadata {
  datasource_definition_attributes?: DatasourceDefinitionAttribute[];
}

interface InventoryDataItem {
  source: string;
  findings: number;
  related_members: string[];
}

interface GenericWidgetData {
  generic_widget_data?: {
    metadata?: string | SourcesMetadata;
    data?: InventoryDataItem[];
  };
}

interface ProcessedSource extends EntityItem {
  displayName?: string;
  logo?: string;
  count?: number;
  filterField?: string[];
}

const InventoryFindingsByDatasources: React.FC<
  InventoryFindingsByDatasourcesProps
> = ({
  selectedScope,
  filterToExecute,
  scopeToExecute,
  isForExport,
  setIsReadyToExport = null,
}) => {
  const { getDataSourceIcon } = useIcon(isForExport ? "LIGHT" : undefined);
  const { data, error, loading, refetch } = useQueryWithRetries({
    query: GET_INVENTORY_FINDINGS_BY_DATASOURCES,

    variables: {
      widget_definition_key: "inventory_findings_by_datasource",
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute || null,
        scopesid: selectedScope?.value?.id,
      },
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });

  const { onFilterClick } = useWidgetFilterNavigator({
    filterToExecute: filterToExecute,
    filterId: null,
    scopeToExecute: scopeToExecute,
    scopeId: selectedScope?.value?.id,
  });

  const handleClick = (source: EntityItem) => {
    onFilterClick([
      ["in", "source", [...((source?.filterField as string[]) ?? [])]],
    ]);
  };

  const processedData = useMemo(() => {
    const widgetData = data as GenericWidgetData | null;
    if (!!widgetData) {
      let sourcesMetadata: string | SourcesMetadata | undefined =
        widgetData?.generic_widget_data?.metadata;
      let inventoryData = widgetData.generic_widget_data?.data;

      try {
        sourcesMetadata = JSON.parse(sourcesMetadata as string);
      } catch (e) {
        console.error("Failed to parse inventory data", e);
      }

      const dataArray = inventoryData
        ?.map((item) => {
          const { source: sourceId, findings, related_members } = item;
          const source = (
            sourcesMetadata as SourcesMetadata
          )?.datasource_definition_attributes?.find(
            (source) => source.id?.toString() === sourceId
          );
          return {
            displayName: source?.display_name,
            logo: getDataSourceIcon(source?.name as string, "S") as
              | string
              | undefined,
            count: findings,
            filterField: related_members,
          };
        })
        ?.reduce<ProcessedSource[]>((acc, item) => {
          // to merge Wiz and Wiz Vulnerabilities - Remove ASAP
          if (!item.displayName) {
            return acc;
          } // Skip items without a displayName
          const existing = acc.find(
            (entry) => entry.displayName === item.displayName
          );
          if (existing) {
            existing.count = (existing.count ?? 0) + (item.count ?? 0); // Merge counts
          } else {
            acc.push(item);
          }
          return acc;
        }, []);

      return dataArray;
    }
    return null;
  }, [data, getDataSourceIcon]);

  return (
    <InventoryKPI
      data={processedData}
      tooltip={(source) =>
        `${source?.displayName}: ${formatNumber(source?.count)} Finding${(source?.count ?? 0) > 1 ? "s" : ""}`
      }
      onEntityClick={handleClick}
      isForExport={isForExport}
      error={error}
      loading={loading}
      refetch={refetch}
      setIsReadyToExport={setIsReadyToExport}
    />
  );
};

export default InventoryFindingsByDatasources;
